import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import {
  CreateWorkOrderRequest,
  ListWorkOrdersQuery,
  UpdateWorkOrderRequest,
  WorkOrder,
  WorkOrderCalendarQueryParams,
  WorkOrderComplementBenefitListRequest,
  WorkOrderMigrateConsultListRequest,
} from './WorkOrders.types';

import {
  listWorkOrders,
  createWorkOrder,
  updateWorkOrder,
  getWorkOrder,
  getWorkOrderCalendar,
  replicateWorkOrder,
  createWorkOrderByPorto,
  workOrdersMigrateConsult,
  workOrdersMigrateConsultItens,
  workOrderCustomerAddressUpdate,
  workOrderPartnerDiscount,
  workOrderComplementBenefitList,
  workOrderEmployeerDiscount,
  workOrderAddPolicy,
  workOrderSystemOriginList,
  workOrdersMigrateConsultBindComplement,
} from './WorkOrders.service';

import { DEFAULT_ROWS, workorderDicionaryError } from '..';
import notificationError from '@/utils/functions/notificationError';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import notificationFieldErrorWorkOrder from '@/utils/functions/showErrorNotification';
import { notification } from 'antd';
import { queryClient } from '@/Root';
import { WORKORDER_COMPLEMENT_DETAIL_KEY } from '../WorkOrdersComplements/WorkOrdersComplements.hooks';

export const QUERY_WORKORDER_MIGRATE_CONSULT = 'work-order/migrate/consult';
export const QUERY_WORKORDER_MIGRATE_CONSULT_ITEMS =
  'work-order/migrate/consult/items';
export const QUERY_WORKORDER_SYSTEM_ORIGIN_LIST_KEY =
  'work-order/system-origin/list';

export function useWorkOrders(params?: ListWorkOrdersQuery, enabled = true) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData(['workOrders/list', { ...params }]);

  const query = useInfiniteQuery(
    ['workOrders/list', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest<'number'> = {
        nextToken: pageParam,
        order: params?.order || 1,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listWorkOrders(req);
    },
    {
      enabled: enabled && !cache,
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useWorkOrder(id?: number, enabled = true) {
  const query = useQuery(['workOrders', id], () => getWorkOrder(id), {
    enabled: !!id && enabled,
    onError(error) {
      notificationError(error);
    },
  });

  return query;
}

export function useWorkOrderMutation() {
  const query = useMutation(getWorkOrder);

  useServiceError(query.error);

  return query;
}

export function useCreateWorkOrder() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: CreateWorkOrderRequest) => createWorkOrder(obj),
    {
      onSuccess() {
        queryChanel({
          resetQueries: [
            ['dashboard-data'],
            ['customers/reduced'],
            ['customers'],
            ['workOrders'],
            ['customer/detail/general'],
            ['customer/detail/reduced'],
            ['workOrders/list'],
          ],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateWorkOrder(id: number) {
  const queryClient = useQueryClient();
  const workOrder = queryClient.getQueryData<WorkOrder>(['workOrders', id]);

  return useMutation(
    (obj: UpdateWorkOrderRequest) => updateWorkOrder(obj, id),
    {
      async onSuccess() {
        if (workOrder?.customer?.id) {
          await queryClient.invalidateQueries([
            'customer/detail',
            workOrder.customer.id,
          ]);
          await queryClient.invalidateQueries(['workOrders', workOrder.id]);
          queryClient.resetQueries(['workOrders']);
          queryClient.removeQueries(['workOrders']);
          queryClient.removeQueries(['workOrders/list']);
          queryClient.removeQueries([
            'customer/detail/general',
            workOrder.customer.id,
          ]);
        }
      },

      onError(error) {
        notificationFieldErrorWorkOrder({
          error,
          messageDefault: () =>
            notificationError(error, {
              translate: (errors) =>
                errors.map((err) => workorderDicionaryError[err] || err),
            }),
        });
      },
    },
  );
}

export function useWorkOrderCalendar(params: WorkOrderCalendarQueryParams) {
  const queryClient = useQueryClient();

  const query = useQuery(
    ['workOrders/calendar', { ...params }],
    () => getWorkOrderCalendar(params),
    {
      enabled: !!params.dateType && !!params.startDate && !!params.endDate,
      onSuccess() {
        queryClient.removeQueries(['workOrders']);
        queryClient.removeQueries(['workOrders/list']);
      },
    },
  );

  useServiceError(query.error);

  return query;
}

export function useReplicateWorkOrder() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(replicateWorkOrder, {
    onSuccess() {
      queryChanel({
        resetQueries: [['dashboard-data'], ['workOrders'], ['workOrders/list']],
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useWorkOrderCreateByPorto() {
  return useMutation({
    mutationFn: createWorkOrderByPorto,
    onSuccess() {
      notification.success({
        message: 'Orçamento vinculado a apolice cadastrado',
      });
    },
    onError(err) {
      notificationError(err);
    },
  });
}

export function useWorkOrderMigrateConsult(
  params?: WorkOrderMigrateConsultListRequest,
  enabled = true,
) {
  const queryKey = [QUERY_WORKORDER_MIGRATE_CONSULT, { ...params }];
  // const cache = queryClient.getQueryData(queryKey);

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      workOrdersMigrateConsult({ rows: 10, ...params, nextToken: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextToken,
    onError(error) {
      notificationError(error);
    },
    enabled: enabled,
  });
}

export function useWorkOrderMigrateConsultItems(
  consultId?: number,
  enabled = true,
) {
  const queryKey = [QUERY_WORKORDER_MIGRATE_CONSULT_ITEMS, { consultId }];
  const cache = queryClient.getQueryData(queryKey);

  return useQuery({
    queryKey,
    queryFn: () => workOrdersMigrateConsultItens(consultId),
    onError(error) {
      notificationError(error);
    },
    enabled: enabled && !cache,
  });
}

export function useWorkOrderCustomerAddressUpdate() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: workOrderCustomerAddressUpdate,
    onSuccess(_, variables) {
      notification.success({
        message: 'Edição de endereço',
        description:
          'Endereço do cliente atualizado com sucesso neste orçamento.',
      });

      queryChanel({
        invalidateQueries: [['workOrders', variables.workOrderId]],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderPartnerDiscount() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: workOrderPartnerDiscount,
    onSuccess(_, vars) {
      notification.success({
        message: 'Situação de desconto atualizado com sucesso',
      });
      queryChanel({ resetQueries: [['workOrders', vars.workOrderId]] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderComplementBenefitList(
  params: WorkOrderComplementBenefitListRequest,
  enabled = true,
) {
  return useQuery({
    queryKey: ['WorkOrder/Complements/Benefits', { ...params }],
    queryFn: () => workOrderComplementBenefitList(params),
    enabled: !!params.workOrderId && enabled,
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderEmployeerDiscount() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: workOrderEmployeerDiscount,
    onSuccess(_, vars) {
      notification.success({
        message:
          'Situação de desconto de funcionário Porto atualizado com sucesso',
      });
      queryChanel({ resetQueries: [['workOrders', vars.workOrderId]] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export default function useWorkOrderAddPolicy() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: workOrderAddPolicy,
    onSuccess() {
      notification.success({ message: 'Apólice vinculada ao orçamento' });

      queryChanel({
        invalidateQueries: [[WORKORDER_COMPLEMENT_DETAIL_KEY], ['workOrders']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderSystemOriginList() {
  return useQuery({
    queryKey: [QUERY_WORKORDER_SYSTEM_ORIGIN_LIST_KEY],
    queryFn: () => workOrderSystemOriginList(),
  });
}

export function useWorkOrderMigrationConsultBindComplement() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: workOrdersMigrateConsultBindComplement,
    onSuccess() {
      notification.success({
        message: 'O complemento do orçamento foi vinculado com sucesso!',
      });

      queryChanel({ invalidateQueries: [[QUERY_WORKORDER_MIGRATE_CONSULT]] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
